import React, { useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useTranslation } from "../i18n";
import { Home } from "lucide-react";
import "./WelcomePage.css";

interface WelcomePageProps {
  deviceId?: string;
  qrSecret?: string;
}

const WelcomePage: React.FC<WelcomePageProps> = ({ deviceId, qrSecret }) => {
  const navigate = useNavigate();
  const { t, language, setLanguage } = useTranslation();
  const [privacyAcknowledged, setPrivacyAcknowledged] = useState(true);

  const handleTapToStart = () => {
    // Prevent navigation if privacy checkbox is not checked
    if (!privacyAcknowledged) {
      return;
    }

    // Navigate to the appropriate home page based on whether we have deviceId/qrSecret
    if (deviceId) {
      navigate(
        `/virtual-intercom/${deviceId}/home${
          qrSecret ? `?code=${qrSecret}` : ""
        }`
      );
    } else {
      navigate("/home");
    }
  };

  return (
    <div className="welcome-container">
      <div className="welcome-content">
        {/* Header with icon and subtitle */}
        <div className="welcome-header">
          <div className="welcome-icon">
            <Home size={56} strokeWidth={1.5} />
          </div>
          <h1 className="welcome-logo-text">{t.welcome.title}</h1>
        </div>

        {/* Welcome message */}
        <div className="welcome-message-section">
          <h2 className="welcome-title">{t.welcome.welcomeTitle}</h2>
          <p className="welcome-description">{t.welcome.description}</p>
        </div>

        {/* Language Switcher - Capsule style */}
        <div className="language-switcher">
          <button
            onClick={() => setLanguage("en")}
            className={`language-btn ${language === "en" ? "active" : ""}`}
          >
            {t.language.english}
          </button>
          <button
            onClick={() => setLanguage("fr")}
            className={`language-btn ${language === "fr" ? "active" : ""}`}
          >
            {t.language.french}
          </button>
          <button
            onClick={() => setLanguage("es")}
            className={`language-btn ${language === "es" ? "active" : ""}`}
          >
            {t.language.spanish}
          </button>
        </div>

        {/* Tap to start button */}
        <div className="welcome-action-section">
          <button
            className="tap-to-start-button"
            onClick={handleTapToStart}
            disabled={!privacyAcknowledged}
          >
            <div className="button-content">
              <span className="button-text">{t.welcome.tapToStart}</span>
              <div className="button-icon">▶</div>
            </div>
          </button>
        </div>

        {/* Privacy notice */}
        <div className="privacy-notice">
          <label className="privacy-checkbox-label">
            <input
              type="checkbox"
              checked={privacyAcknowledged}
              onChange={(e) => setPrivacyAcknowledged(e.target.checked)}
              className="privacy-checkbox"
            />
            <span className="privacy-text">
              A snapshot image may be captured during the intercom call for
              security record purposes. Images are securely stored and
              automatically deleted after 30 days.{" "}
              <a
                href="https://www.entrioai.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="privacy-link"
              >
                Privacy Policy
              </a>
            </span>
          </label>
        </div>
      </div>
    </div>
  );
};

// Wrapper component to handle URL parameters
const WelcomePageWithParams: React.FC = () => {
  const { deviceId } = useParams<{ deviceId?: string }>();
  const [searchParams] = useSearchParams();
  const qrSecret = searchParams.get("code");

  return <WelcomePage deviceId={deviceId} qrSecret={qrSecret || undefined} />;
};

export default WelcomePage;
export { WelcomePageWithParams };
